import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useUniAdminAuth } from "../../context/UniAdminAuthContext";
import { PERMISSIONS } from "../../utils/permissionKeys";
import UMTransactionList from "../../features/university-manager/UMTransactionList";
import DemographicList from "../../components/common/DemographicList";
import umService from "../../services/university-admin/umService";

const ClubDetailModal = ({
  club,
  onClose,
  onEdit,
  onManageMembers,
  onManageEvents,
}) => {
  const { t } = useTranslation(["clubs", "common"]);
  const { can } = useUniAdminAuth();
  const [activeTab, setActiveTab] = useState("overview");
  const [clubStats, setClubStats] = useState(null);
  const [clubStatsLoading, setClubStatsLoading] = useState(false);

  useEffect(() => {
    if (activeTab !== "exports" || !club?.id || clubStats || !can(PERMISSIONS.club.statistics)) return;
    let cancelled = false;
    setClubStatsLoading(true);
    umService.getClubStatistics(club.id).then((res) => {
      if (!cancelled) setClubStats(res.success ? res.data : null);
    }).finally(() => {
      if (!cancelled) setClubStatsLoading(false);
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, club?.id]);

  if (!club) return null;

  const getStatusBadge = (active) => (
    <span className={`badge ${active ? "bg-success" : "bg-danger"} fs-6`}>
      <i
        className={`fas ${active ? "fa-check-circle" : "fa-times-circle"} me-1`}
      ></i>
      {active ? t("activeClub") : t("inactiveClub")}
    </span>
  );

  return (
    <div
      className="modal show d-block"
      style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
    >
      <div className="modal-dialog modal-xl">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="fas fa-users me-2"></i>
              {t("clubDetails")}
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
            ></button>
          </div>
          <div className="modal-body">
            <ul className="nav nav-tabs mb-4">
              <li className="nav-item">
                <button className={`nav-link ${activeTab === "overview" ? "active fw-bold" : ""}`} onClick={() => setActiveTab("overview")}>{t("common:tabs.overview")}</button>
              </li>
              <li className="nav-item">
                <button className={`nav-link ${activeTab === "financials" ? "active fw-bold" : ""}`} onClick={() => setActiveTab("financials")}>{t("common:tabs.financials")}</button>
              </li>
              <li className="nav-item">
                <button className={`nav-link ${activeTab === "exports" ? "active fw-bold" : ""}`} onClick={() => setActiveTab("exports")}>{t("common:tabs.exports")}</button>
              </li>
            </ul>

            {activeTab === "overview" && (
              <div className="row">
                <div className="col-md-8">
                {/* Club Header */}
                <div className="d-flex align-items-center mb-4">
                  <div
                    className="club-logo rounded-circle bg-primary bg-opacity-10 d-flex align-items-center justify-content-center me-3"
                    style={{ width: "80px", height: "80px" }}
                  >
                    {club.imageUrl ? (
                      <img
                        src={club.imageUrl}
                        alt={club.nameEn}
                        className="rounded-circle"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <i className="fas fa-users text-primary fs-2"></i>
                    )}
                  </div>
                  <div>
                    <div className="mb-1">
                      <span
                        className="badge bg-secondary bg-opacity-25 text-secondary me-1"
                        style={{ fontSize: "0.65rem" }}
                      >
                        EN
                      </span>
                      <span className="fw-bold text-dark fs-5">
                        {club.nameEn}
                      </span>
                    </div>
                    {club.nameLocal && (
                      <div className="mb-2">
                        <span
                          className="badge bg-secondary bg-opacity-25 text-secondary me-1"
                          style={{ fontSize: "0.65rem" }}
                        >
                          {t("local")}
                        </span>
                        <span className="text-muted fs-6">
                          {club.nameLocal}
                        </span>
                      </div>
                    )}
                    <div className="d-flex gap-2 flex-wrap">
                      <span className="badge bg-info fs-6">
                        <i className="fas fa-building me-1"></i>
                        {club.campusNameEn}
                      </span>
                      <span className="badge bg-primary fs-6">
                        <i className="fas fa-university me-1"></i>
                        {club.universityNameEn}
                      </span>
                      {getStatusBadge(club.status)}
                    </div>
                  </div>
                </div>

                {/* Descriptions */}
                {(club.descriptionEn || club.descriptionLocal) && (
                  <div className="card border-0 bg-light mb-4">
                    <div className="card-body">
                      {club.descriptionEn && (
                        <div className={club.descriptionLocal ? "mb-3" : ""}>
                          <label className="form-label fw-bold text-muted small text-uppercase">
                            <span
                              className="badge bg-secondary bg-opacity-25 text-secondary me-1"
                              style={{ fontSize: "0.65rem" }}
                            >
                              EN
                            </span>
                            {t("fields.descriptionEn")}
                          </label>
                          <p className="mb-0">{club.descriptionEn}</p>
                        </div>
                      )}
                      {club.descriptionLocal && (
                        <div>
                          <label className="form-label fw-bold text-muted small text-uppercase">
                            <span
                              className="badge bg-secondary bg-opacity-25 text-secondary me-1"
                              style={{ fontSize: "0.65rem" }}
                            >
                              {t("local")}
                            </span>
                            {t("fields.descriptionLocal")}
                          </label>
                          <p className="mb-0">{club.descriptionLocal}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Location Hierarchy */}
                <div className="card border-0 bg-light mb-4">
                  <div className="card-body">
                    <h6 className="fw-bold mb-3">
                      <i className="fas fa-map-marker-alt me-2 text-primary"></i>
                      {t("locationHierarchy")}
                    </h6>
                    <div className="club-hierarchy">
                      <div className="d-flex align-items-center mb-2">
                        <i className="fas fa-university text-primary me-2"></i>
                        <span className="fw-medium">
                          {t("fields.university")}:
                        </span>
                        <span className="ms-2">{club.universityNameEn}</span>
                      </div>
                      <div className="d-flex align-items-center mb-2">
                        <i className="fas fa-building text-info me-2"></i>
                        <span className="fw-medium">{t("fields.campus")}:</span>
                        <span className="ms-2">{club.campusNameEn}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Categories */}
                {club.categories && club.categories.length > 0 && (
                  <div className="card border-0 bg-light mb-4">
                    <div className="card-body">
                      <h6 className="fw-bold mb-3">
                        <i className="fas fa-tags me-2 text-primary"></i>
                        {t("categories")}
                      </h6>
                      <div className="d-flex flex-wrap gap-2">
                        {club.categories.map((category) => (
                          <span
                            key={category.id}
                            className="badge bg-secondary fs-6"
                          >
                            <i className="fas fa-tag me-1"></i>
                            {category.name}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Contact Information */}
                {(club.contactEmail ||
                  club.communication?.email ||
                  club.communication?.phone_number ||
                  club.communication?.phone ||
                  club.communication?.instagram ||
                  club.communication?.twitter ||
                  club.communication?.whatsapp_phone_number ||
                  club.communication?.whatsappPhoneNumber) && (
                  <div className="row mb-4">
                    <div className="col-12">
                      <h6 className="fw-bold mb-3">
                        {t("contactInformation")}
                      </h6>
                    </div>
                    {club.contactEmail && (
                      <div className="col-md-6 mb-3">
                        <label className="form-label fw-bold text-muted small">
                          {t("primaryEmail")}
                        </label>
                        <p className="text-dark">
                          <i className="fas fa-envelope me-2"></i>
                          <a
                            href={`mailto:${club.contactEmail}`}
                            className="text-decoration-none"
                          >
                            {club.contactEmail}
                          </a>
                        </p>
                      </div>
                    )}
                    {club.communication?.email && (
                      <div className="col-md-6 mb-3">
                        <label className="form-label fw-bold text-muted small">
                          {t("additionalEmail")}
                        </label>
                        <p className="text-dark">
                          <i className="fas fa-envelope me-2"></i>
                          {club.communication.email}
                        </p>
                      </div>
                    )}
                    {(club.communication?.phone_number ||
                      club.communication?.phone) && (
                      <div className="col-md-6 mb-3">
                        <label className="form-label fw-bold text-muted small">
                          {t("fields.phone").toUpperCase()}
                        </label>
                        <p className="text-dark">
                          <i className="fas fa-phone me-2"></i>
                          {club.communication.phone_number ||
                            club.communication.phone}
                        </p>
                      </div>
                    )}
                    {(club.communication?.whatsapp_phone_number ||
                      club.communication?.whatsappPhoneNumber) && (
                      <div className="col-md-6 mb-3">
                        <label className="form-label fw-bold text-muted small">
                          {t("whatsapp")}
                        </label>
                        <p className="text-dark">
                          <i className="fab fa-whatsapp me-2"></i>
                          {club.communication.whatsapp_phone_number ||
                            club.communication.whatsappPhoneNumber}
                        </p>
                      </div>
                    )}
                    {club.communication?.instagram && (
                      <div className="col-md-6 mb-3">
                        <label className="form-label fw-bold text-muted small">
                          {t("instagram")}
                        </label>
                        <p className="text-dark">
                          <i className="fab fa-instagram me-2"></i>@
                          {club.communication.instagram}
                        </p>
                      </div>
                    )}
                    {club.communication?.twitter && (
                      <div className="col-md-6 mb-3">
                        <label className="form-label fw-bold text-muted small">
                          {t("twitter")}
                        </label>
                        <p className="text-dark">
                          <i className="fab fa-twitter me-2"></i>@
                          {club.communication.twitter}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Price */}
                {club.price != null && (
                  <div className="row">
                    <div className="col-12">
                      <label className="form-label fw-bold text-muted small">
                        {t("fields.price")}
                      </label>
                      <p className="text-dark fs-5">
                        {club.price === 0 ? t("free") : `${club.price} ₺`}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Statistics Panel */}
              <div className="col-md-4">
                <div className="card border-0 bg-light h-100">
                  <div className="card-body">
                    <h6 className="fw-bold mb-4">
                      <i className="fas fa-chart-bar me-2"></i>
                      {t("clubStatistics")}
                    </h6>

                    {/* Members */}
                    <div className="d-flex align-items-center mb-4">
                      <div
                        className="bg-primary rounded-circle d-flex align-items-center justify-content-center me-3"
                        style={{ width: "55px", height: "55px" }}
                      >
                        <i className="fas fa-users text-white fs-4"></i>
                      </div>
                      <div>
                        <div className="fw-bold fs-2">
                          {club.totalMembers || 0}
                        </div>
                        <small className="text-muted">
                          {t("statistics.totalMembers")}
                        </small>
                        <div className="small text-muted">
                          {club.totalAdmins || 0}{" "}
                          {t("admin", { count: club.totalAdmins || 0 })},{" "}
                          {club.totalModerators || 0}{" "}
                          {t("moderator", { count: club.totalModerators || 0 })}
                        </div>
                      </div>
                    </div>

                    {/* Events */}
                    <div className="d-flex align-items-center mb-4">
                      <div
                        className="bg-success rounded-circle d-flex align-items-center justify-content-center me-3"
                        style={{ width: "55px", height: "55px" }}
                      >
                        <i className="fas fa-calendar-alt text-white fs-4"></i>
                      </div>
                      <div>
                        <div className="fw-bold fs-2">
                          {club.totalEvents || 0}
                        </div>
                        <small className="text-muted">
                          {t("statistics.totalEvents")}
                        </small>
                        <div className="small text-muted">
                          {club.upcomingEventsNumber || 0} {t("upcoming")},{" "}
                          {club.pastEventsNumber || 0} {t("completed")},{" "}
                          {club.cancelledEventsNumber || 0} {t("cancelled")}
                        </div>
                      </div>
                    </div>

                    {/* Status Card */}
                    <div className="card border-0 bg-white mb-4">
                      <div className="card-body text-center py-3">
                        <div
                          className={`${
                            club.status ? "bg-success" : "bg-danger"
                          } rounded-circle d-flex align-items-center justify-content-center mx-auto mb-2`}
                          style={{ width: "40px", height: "40px" }}
                        >
                          <i
                            className={`fas ${club.status ? "fa-check" : "fa-times"} text-white`}
                          ></i>
                        </div>
                        <h6 className="fw-bold mb-1">
                          {club.status ? t("activeClub") : t("inactiveClub")}
                        </h6>
                        <small className="text-muted">
                          {club.status
                            ? t("clubActiveDescription")
                            : t("clubInactiveDescription")}
                        </small>
                      </div>
                    </div>

                    {/* Quick Actions */}
                    {(can(PERMISSIONS.membership.listElevated, club.id) || can(PERMISSIONS.event.list)) && (
                      <div className="border-top pt-3">
                        <h6 className="fw-bold mb-3">{t("quickActions")}</h6>
                        <div className="d-grid gap-2">
                          {can(PERMISSIONS.membership.listElevated, club.id) && (
                            <button
                              className="btn btn-outline-primary btn-sm"
                              onClick={() => {
                                onClose();
                                onManageMembers(club);
                              }}
                            >
                              <i className="fas fa-users me-2"></i>
                              {t("manageMembers")}
                            </button>
                          )}
                          {can(PERMISSIONS.event.list) && (
                            <button
                              className="btn btn-outline-success btn-sm"
                              onClick={() => {
                                onClose();
                                onManageEvents(club);
                              }}
                            >
                              <i className="fas fa-calendar-alt me-2"></i>
                              {t("manageEvents")}
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            )}

            {activeTab === "financials" && (
              <UMTransactionList clubId={club.id} title="Club Financials" />
            )}

            {activeTab === "exports" && (
              <div className="p-4">
                {can(PERMISSIONS.club.statistics) && (
                  <div className="mb-4 pb-4 border-bottom">
                    <h6 className="fw-bold mb-3">
                      <i className="fas fa-chart-bar me-2 text-primary"></i>
                      {t("common:statisticsPanel.title")}
                    </h6>
                    {clubStatsLoading ? (
                      <div className="text-center py-3">
                        <div className="spinner-border spinner-border-sm text-primary"></div>
                      </div>
                    ) : clubStats ? (
                      <div className="row">
                        <div className="col-12 mb-3">
                          <div className="row text-center">
                            <div className="col-md-6 mb-2">
                              <div className="fw-bold fs-4 text-success">{clubStats.activeMembers ?? 0}</div>
                              <small className="text-muted">{t("common:statisticsPanel.activeMembers")}</small>
                            </div>
                            <div className="col-md-6 mb-2">
                              <div className="fw-bold fs-4 text-info">{clubStats.totalAttendances ?? 0}</div>
                              <small className="text-muted">{t("common:statisticsPanel.totalAttendances")}</small>
                            </div>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <DemographicList title={t("common:statisticsPanel.byGrade")} icon="fa-graduation-cap" items={clubStats.byGrade} emptyLabel={t("common:statisticsPanel.noBreakdownData")} translateLabel={(label) => t(`users:grade.${label}`, { defaultValue: label })} />
                          <DemographicList title={t("common:statisticsPanel.byDepartment")} icon="fa-building" items={clubStats.byDepartment} emptyLabel={t("common:statisticsPanel.noBreakdownData")} />
                        </div>
                        <div className="col-md-6">
                          <DemographicList title={t("common:statisticsPanel.byGender")} icon="fa-venus-mars" items={clubStats.byGender} emptyLabel={t("common:statisticsPanel.noBreakdownData")} translateLabel={(label) => t(`users:gender.${label}`, { defaultValue: label })} />
                        </div>
                        {clubStats.events && clubStats.events.length > 0 && (
                          <div className="col-12 mt-2">
                            <h6 className="fw-bold mb-2 small text-uppercase text-muted">{t("common:statisticsPanel.eventsBreakdown")}</h6>
                            <div className="table-responsive">
                              <table className="table table-sm">
                                <thead>
                                  <tr>
                                    <th>{t("common:statisticsPanel.eventName")}</th>
                                    <th>{t("common:statisticsPanel.date")}</th>
                                    <th>{t("common:statisticsPanel.location")}</th>
                                    <th className="text-end">{t("common:statisticsPanel.participants")}</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {clubStats.events.map((ev) => (
                                    <tr key={ev.eventId}>
                                      <td>{ev.eventTitle}</td>
                                      <td>{new Date(ev.date).toLocaleDateString()}</td>
                                      <td>{ev.location}</td>
                                      <td className="text-end">{ev.participants}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="text-muted small">{t("common:statisticsPanel.noData")}</p>
                    )}
                  </div>
                )}
                <h6 className="fw-bold mb-4">{t("common:exports.clubTitle")}</h6>
                <div className="d-flex flex-wrap gap-3">
                  <button className="btn btn-outline-danger" onClick={() => umService.exportClubStatistics(club.id)}>
                    <i className="fas fa-file-pdf me-2"></i> {t("common:exports.statisticsPdf")}
                  </button>
                  <button className="btn btn-outline-success" onClick={() => umService.exportClubMembersDirectory(club.id, 'EXCEL')}>
                    <i className="fas fa-file-excel me-2"></i> {t("common:exports.membersDirectoryExcel")}
                  </button>
                  <button className="btn btn-outline-info" onClick={() => umService.exportClubMembersDirectory(club.id, 'CSV')}>
                    <i className="fas fa-file-csv me-2"></i> {t("common:exports.membersDirectoryCsv")}
                  </button>
                </div>
              </div>
            )}
          </div>
          <div className="modal-footer">
            {can(PERMISSIONS.club.update) && (
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={() => {
                  onClose();
                  onEdit(club);
                }}
              >
                <i className="fas fa-edit me-2"></i>
                {t("editClub")}
              </button>
            )}
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
            >
              {t("common:buttons.close")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClubDetailModal;
